import { usePostSendEmail } from '@api/hooks';
import { CTAButton } from '@components';
import { InputLabel, TextField } from '@mui/material';
import { useRouter } from 'next/router';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styles from './styles.module.scss';

interface FormValue {
  firstName: string;
  lastName: string;
  email: string;
  companySize: string;
  infoSource: string;
}

const ScheduleForm = () => {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isSubmitSuccessful, isDirty },
  } = useForm<FormValue>();

  const { mutate, isLoading } = usePostSendEmail({
    onSuccess: () => {
      reset();
    },
  });

  const onSubmit: SubmitHandler<FormValue> = (data) => {
    mutate({ type: 'form', page: router.asPath, ...data });
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.title}>Schedule a Demo</div>
      <div className={styles.description}>
        Simply fill out the form and we’ll be in touch soon.
      </div>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.nameFieldContainer}>
          <div className={styles.firstNameContainer}>
            <InputLabel className={styles.inputLabel}>First Name*</InputLabel>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: { value: true, message: 'Required Field' } }}
              render={({ fieldState: { error } }) => (
                <TextField
                  {...register('firstName')}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  placeholder="First name"
                  error={!!error}
                  helperText={error?.message}
                  FormHelperTextProps={{ style: { fontSize: '1.2rem' } }}
                />
              )}
            />
          </div>
          <div className={styles.lastNameContainer}>
            <InputLabel className={styles.inputLabel}>Last Name*</InputLabel>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: { value: true, message: 'Required Field' } }}
              render={({ fieldState: { error } }) => (
                <TextField
                  {...register('lastName')}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  placeholder="Last name"
                  error={!!error}
                  helperText={error?.message}
                  FormHelperTextProps={{ style: { fontSize: '1.2rem' } }}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.inputFieldContainer}>
          <InputLabel className={styles.inputLabel}>Email*</InputLabel>
          <Controller
            name="email"
            control={control}
            rules={{
              required: { value: true, message: 'Required Field' },
              pattern: {
                value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                message: 'Invalid Email Format',
              },
            }}
            render={({ fieldState: { error } }) => (
              <TextField
                {...register('email')}
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                placeholder="Email"
                helperText={error?.message}
                FormHelperTextProps={{ style: { fontSize: '1.2rem' } }}
              />
            )}
          />
        </div>
        <div className={styles.inputFieldContainer}>
          <InputLabel className={styles.inputLabel}>How did you hear about us?</InputLabel>
          <Controller
            name="infoSource"
            control={control}
            render={() => (
              <TextField
                {...register('infoSource')}
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                placeholder="Tell us anything"
              />
            )}
          />
        </div>
        <CTAButton type="submit" className={styles.button} isLoading={isLoading}>
          {isSubmitSuccessful && !isDirty ? 'Sent' : 'Get a demo'}
        </CTAButton>
      </form>
    </div>
  );
};

export default ScheduleForm;
